import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiCall from "../../../../utils/apiCall";
import { styled } from "@mui/system";



const ResetSchema = Yup.object().shape({
  ApplicantId: Yup.string().required("Applicant Id is required"),
  Password: Yup.string().required("Password is required"),
});

const StyledTextField = styled(TextField)({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "50px",
    fontSize: "16px",
  },
  "& .MuiInputBase-input": {
    padding: "12px",
  },
});

const StyledDialogActions = styled(DialogActions)({
  padding: "10px",
  justifyContent: "space-between",
});
interface ResetStudentFormProps {
  open: boolean;
  onClose: () => void;
  applicantId?: string | number | null; // make it optional and compatible with your API
}

const ResetStudentForm: React.FC<ResetStudentFormProps> = ({
  open,
  onClose,
  applicantId, // <-- add this here
}) => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<"success" | "error" | null>(null);
  const [formKey, setFormKey] = useState(Date.now()); // Forces form reset on modal open

  const { mutate: resetPassword, isLoading } = useMutation({
    mutationKey: ["reset-Applicant-password"],
    mutationFn: async (data: { ApplicantId: string; Password: string }) => {
      const response = await apiCall().post("/Applicant/AdminResetPasswordMainAsync", data);
      return response.data;
    },
    onSuccess: (data) => {
      setMessage(data.Message);
      setMessageType(data.StatusCode === 200 ? "success" : "error");
      queryClient.invalidateQueries(["student-reset"]);
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.Message || "An error occurred");
      setMessageType("error");
    },
  });

  const handleCloseSnackbar = () => {
    setMessage(null);
    setMessageType(null);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setFormKey(Date.now());
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          backgroundColor: "darkgreen",
          color: "white",
          fontWeight: "bold",
          textAlign: "center",
        }}
      >
        Reset Applicant Password
        <IconButton
          aria-label="close"
          onClick={() => {
            onClose();
            setFormKey(Date.now());
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            backgroundColor: "darkgreen",
            color: "white",
            "&:hover": { backgroundColor: "green" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={{
          ApplicantId: applicantId ?? "",
          Password: "",
        }}
        validationSchema={ResetSchema}
        onSubmit={(values, { resetForm }) => {
          resetPassword(
            {
              ApplicantId: String(values.ApplicantId).trim(),
              Password: values.Password,
            },
            {
              onSuccess: () => resetForm(),
            }
          );
        }}
      >
        {({ handleSubmit, errors, touched }) => (
          <Form onSubmit={handleSubmit}>
            <DialogContent sx={{ padding: "20px" }}>
              <Box display="flex" flexDirection="column" gap="20px">
                <Field
                  as={StyledTextField}
                  name="ApplicantId"
                  label="Applicant ID"
                  disabled
                  error={touched.ApplicantId && Boolean(errors.ApplicantId)}
                  helperText={touched.ApplicantId && errors.ApplicantId}
                />

                <Field
                  as={StyledTextField}
                  name="Password"
                  label="Set Password for Applicant"
                  type="password"
                  error={touched.Password && Boolean(errors.Password)}
                  helperText={touched.Password && errors.Password}
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center" }}>
              <Button
                type="submit"
                disabled={isLoading}
                sx={{
                  backgroundColor: "darkgreen",
                  color: "white",
                  width: "150px",
                  "&:hover": { backgroundColor: "green" },
                }}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={messageType || "error"} onClose={handleCloseSnackbar}>
          {message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};



export default ResetStudentForm;
